import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { type ApiError, api, type ListResponse } from "@/api/client"
import type { AuthorizeResponse } from "@/api/generated/AuthorizeResponse"
import type { McpServer } from "@/api/generated/McpServer"
import type { McpTestResult } from "@/api/generated/McpTestResult"
import type { McpToolInfo } from "@/api/generated/McpToolInfo"
import { describeError, useApiMutation } from "@/api/mutation"
import i18n from "@/i18n"

export const mcpKeys = {
  all: ["mcp-servers"] as const,
  list: () => [...mcpKeys.all, "list"] as const,
  detail: (id: number) => [...mcpKeys.all, "detail", id] as const,
  tools: (id: number) => [...mcpKeys.all, "tools", id] as const,
}

/** `POST /mcp-servers/discover-oauth` (mit_mcp::oauth::Discovery — not exported by ts-rs). */
export interface OAuthDiscovery {
  authorization_url: string
  token_url: string
  registration_url: string
  resource: string
  scopes: string
  issuer: string
  grant_types_supported: string[]
  token_endpoint_auth_methods_supported: string[]
}

export const mcpServersQuery = () =>
  queryOptions({
    queryKey: mcpKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<McpServer>>("/mcp-servers", { signal }),
    select: (r) => r.items,
  })

export const useMcpServers = () => useQuery(mcpServersQuery())

export const useMcpServer = (id: number | undefined) =>
  useQuery({
    queryKey: mcpKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<McpServer>(`/mcp-servers/${id}`, { signal }),
    enabled: id != null,
  })

/** Cached tool list of an integration (falls back to a live listing server-side). */
export const mcpToolsQuery = (id: number) =>
  queryOptions({
    queryKey: mcpKeys.tools(id),
    queryFn: ({ signal }) => api.get<ListResponse<McpToolInfo>>(`/mcp-servers/${id}/tools`, { signal }),
    select: (r) => r.items,
    retry: false,
    staleTime: 5 * 60_000,
  })

export const useMcpTools = (id: number | undefined, enabled = true) =>
  useQuery({ ...mcpToolsQuery(id ?? 0), enabled: id != null && enabled })

export interface McpInput {
  name?: string
  description?: string
  transport?: "http" | "stdio"
  url?: string
  command?: string
  args?: string[]
  env?: Record<string, string>
  tool_prefix?: string
  /** `null` = all tools. */
  enabled_tools?: string[] | null
  enabled?: boolean
  system_prompt_instructions?: string
  auth_type?: "none" | "headers" | "oauth"
  headers?: Record<string, string>
  oauth_client_id?: string
  oauth_client_secret?: string
  oauth_authorization_url?: string
  oauth_token_url?: string
  oauth_registration_url?: string
  oauth_scopes?: string
  oauth_resource?: string
}

export function useCreateMcp() {
  const { t } = useTranslation()
  return useApiMutation<McpServer, McpInput>({
    mutationFn: (body) => api.post("/mcp-servers", body),
    invalidates: [mcpKeys.all],
    successToast: (s) => t("studio.mcp.created", { name: s.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateMcp() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<McpServer, { id: number; silent?: boolean } & McpInput>({
    mutationFn: ({ id, silent: _s, ...body }) => api.patch(`/mcp-servers/${id}`, body),
    onSuccess: (s) => qc.setQueryData(mcpKeys.detail(s.id), s),
    invalidates: [mcpKeys.all],
    successToast: (s, v) => !v.silent && t("studio.mcp.updated", { name: s.name }),
    errorToast: "unlessFields",
  })
}

export function useDeleteMcp() {
  const { t } = useTranslation()
  return useApiMutation<void, McpServer>({
    mutationFn: (s) => api.delete(`/mcp-servers/${s.id}`),
    invalidates: [mcpKeys.all, ["agents"]],
    successToast: (_d, s) => t("studio.mcp.deleted", { name: s.name }),
  })
}

export function useTestMcp() {
  const qc = useQueryClient()
  return useApiMutation<McpTestResult, number>({
    mutationFn: (id) => api.post(`/mcp-servers/${id}/test`),
    onSuccess: async (r, id) => {
      if (r.ok) qc.setQueryData(mcpKeys.tools(id), { items: r.tools })
      await qc.invalidateQueries({ queryKey: mcpKeys.list() })
      await qc.invalidateQueries({ queryKey: mcpKeys.detail(id) })
    },
  })
}

/** Upstream (502) failures carry a useful message from the remote server; show it. */
function upstreamToast(title: string) {
  return (e: ApiError) =>
    toast.error(title, { description: e.status === 502 ? e.message : describeError(e, i18n.t) })
}

export function useDiscoverOAuth() {
  const { t } = useTranslation()
  return useApiMutation<OAuthDiscovery, string>({
    mutationFn: (url) => api.post("/mcp-servers/discover-oauth", { url }),
    errorToast: false,
    onError: upstreamToast(t("studio.mcp.oauth.discoverFailed")),
  })
}

export function useAuthorizeMcp() {
  const { t } = useTranslation()
  return useApiMutation<AuthorizeResponse, number>({
    mutationFn: (id) => api.post(`/mcp-servers/${id}/oauth/authorize`),
    errorToast: false,
    onError: upstreamToast(t("studio.mcp.oauth.error")),
  })
}

export function useRevokeMcp() {
  const { t } = useTranslation()
  return useApiMutation<void, McpServer>({
    mutationFn: (s) => api.post(`/mcp-servers/${s.id}/oauth/revoke`),
    invalidates: [mcpKeys.all],
    successToast: (_d, s) => t("studio.mcp.oauth.revoked", { name: s.name }),
  })
}
